import { combineReducers, configureStore } from '@reduxjs/toolkit';
import { ConfigureStoreOptions } from '@reduxjs/toolkit/dist/configureStore';

import {
  benefitsChoiceActions,
  benefitsChoiceReducer,
  benefitsChoiceSelectors,
} from '@/features/benefitsChoice';
import { carsApi, carsReducer, carsSelectors } from '@/features/cars';
import {
  choiceActions,
  choiceMiddleware,
  choiceReducer,
  choiceSelectors,
} from '@/features/choice';
import { configReducer, configSelectors } from '@/features/config';
import { creditActions, creditReducer, creditSelectors } from '@/features/credit';
import { dealersApi, dealersReducer } from '@/features/dealers';
import dictionariesReducer, { dictionariesApi } from '@/features/dictionaries/reducer';
import {
  equipmentsExpandedActions,
  equipmentsExpandedReducer,
  equipmentsExpandedSelectors,
} from '@/features/equipments';
import { filtersApi, filtersReducer, filtersSelectors } from '@/features/filters';
import {
  installationActions,
  installationMiddleware,
  installationReducer,
  installationSelectors,
} from '@/features/installation';
import {
  referencesActions,
  referencesReducer,
  referencesSelectors,
} from '@/features/references';
import mergeDeep from '@/utils/mergeDeep';

export type RootState = ReturnType<typeof rootReducer>;
export type AppStore = ReturnType<typeof setupStore>;
export type AppDispatch = AppStore['dispatch'];

export const rootQueriesApi = {
  cars: carsApi,
  dealers: dealersApi,
  filters: filtersApi,
};

export const rootActions = {
  choice: choiceActions,
  credit: creditActions,
  references: referencesActions,
  benefitsChoice: benefitsChoiceActions,
  equipmentsExpanded: equipmentsExpandedActions,
  installation: installationActions,
};

export const rootSelectors = {
  cars: carsSelectors,
  filters: filtersSelectors,
  choice: choiceSelectors,
  credit: creditSelectors,
  references: referencesSelectors,
  config: configSelectors,
  benefitsChoice: benefitsChoiceSelectors,
  equipmentsExpanded: equipmentsExpandedSelectors,
  installation: installationSelectors,
};

export const rootMiddleware = [...choiceMiddleware, ...installationMiddleware];

const rootReducer = combineReducers({
  choice: choiceReducer,
  config: configReducer,
  credit: creditReducer,
  references: referencesReducer,
  benefitsChoice: benefitsChoiceReducer,
  equipmentsExpanded: equipmentsExpandedReducer,
  installation: installationReducer,
  [dictionariesApi.reducerPath]: dictionariesReducer,
  [filtersApi.reducerPath]: filtersReducer,
  [carsApi.reducerPath]: carsReducer,
  [dealersApi.reducerPath]: dealersReducer,
});

const getStoreConfigurationOptions = (
  devTools = true,
): ConfigureStoreOptions<RootState> => {
  return {
    devTools,
    reducer: rootReducer,
    middleware: getDefaultMiddleware =>
      getDefaultMiddleware({ serializableCheck: false })
        .concat(rootMiddleware)
        .concat(filtersApi.middleware)
        .concat(carsApi.middleware)
        .concat(dealersApi.middleware)
        .concat(dictionariesApi.middleware),
  };
};

export const setupStore = (preloadedState?: Partial<RootState>) => {
  return configureStore({
    ...getStoreConfigurationOptions(),
    preloadedState: preloadedState as RootState,
  });
};

export const getPreloadedStateWithInitialState = (preloadedState: Partial<RootState>) => {
  const storeWithoutPreloadedState = configureStore(getStoreConfigurationOptions(false));
  const initialState = storeWithoutPreloadedState.getState();

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  return mergeDeep(initialState, preloadedState) as RootState;
};
